import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Select, Text } from 'src/@vymo/ui/atoms';
import type { Option } from 'src/@vymo/ui/atoms/select/types';
import { Card, RangePicker } from 'src/@vymo/ui/blocks';
import { Widget } from 'src/@vymo/ui/molecules';
import classnames from 'classnames';
import { format } from 'date-fns';
import { Keys, locale } from 'src/i18n';
import {
  getCurrentUser,
  getRevyreWorkflowConfig,
} from 'src/models/lmsConfig/queries';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { renderDialog } from 'src/workspace/slice';
import { generateRandomCode } from 'src/workspace/utils';
import { getThisWeekRange, getTodayActivity } from '../queries';
import {
  getCalendarData,
  getFilterData,
  selectModifiedActivityTypes,
} from '../selectors';
import {
  fetchCalendarActivityList,
  fetchHomeDashboardCalenderData,
  getCalendarDetails,
  getLeadData,
} from '../thunk';
import ActivityDetails from './activityDetails';
import ActivityListItem from './activityListItem';
import RevyreActivityDetails from './revyreActivityDetails';
import { Activity, ActivityGroup, CalendarParams } from './types';
import styles from './index.module.scss';

function ActivityCalendar() {
  const revyreConfig = getRevyreWorkflowConfig();

  const [selectedActivity, setSelectedActivity] = useState<string | undefined>(
    '',
  );

  const [calendarItem, setCalendarItem] = useState();
  const [isActivityCompleted, setIsActivityCompleted] = useState(false);

  const [taskCode, setTaskCode] = useState('');

  const dispatch = useAppDispatch();

  const [dateRange, setDateRange] = useState<[Date, Date]>(getThisWeekRange());

  const activities = useAppSelector((state) => getCalendarData(state));

  const filterData = useAppSelector((state) => getFilterData(state));

  const activityTypeOptions: Option[] = useAppSelector((state) =>
    selectModifiedActivityTypes(state),
  );

  const getCalendar = useCallback(async () => {
    const [start, end] = dateRange;
    if (!start || !end || !filterData.filterData) return;

    const params: CalendarParams = {
      start,
      end,
      is_completed: true,
      filters: JSON.stringify(filterData?.filterData ?? {}),
    };

    if (taskCode && taskCode.trim()) {
      params.task_codes = JSON.stringify([taskCode]);
    }

    try {
      await dispatch(
        fetchHomeDashboardCalenderData({
          params,
          type: revyreConfig?.length ? 'revyre' : 'legacy',
        }),
      ).unwrap();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Calendar load error:', err);
    }
  }, [
    dateRange,
    filterData.filterData,
    taskCode,
    dispatch,
    revyreConfig?.length,
  ]);

  useEffect(() => {
    const today = new Date();
    const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    const params = {
      code: getCurrentUser().code,
      start: lastMonthEnd.toISOString(),
      end: today.toISOString(),
    };
    dispatch(fetchCalendarActivityList({ params }));
  }, [dispatch]);

  useEffect(() => {
    getCalendar();
  }, [getCalendar]);

  const groupedActivities = useMemo(() => {
    if (!activities || !dateRange) return [];

    const [start, end] = dateRange;

    const startDate = new Date(start);
    const endDate = new Date(end);

    const activityMap = new Map<string, Activity[]>();

    activities.forEach((activity) => {
      const meetingDate = activity?.meeting?.date;
      if (!meetingDate) return;

      const parsedDate = new Date(meetingDate);
      if (Number.isNaN(parsedDate.getTime())) return;

      const dateKey = format(parsedDate, 'yyyy-MM-dd');
      const mappedActivities = activityMap.get(dateKey) || [];

      mappedActivities.push(activity);
      activityMap.set(dateKey, mappedActivities);
    });

    const allDays: ActivityGroup[] = [];

    const current = new Date(startDate);
    while (current <= endDate) {
      const dateKey = format(current, 'yyyy-MM-dd');
      const acts = activityMap.get(dateKey) || [];

      acts.sort(
        (a, b) =>
          new Date(a.meeting.date).getTime() -
          new Date(b.meeting.date).getTime(),
      );

      allDays.push({
        date: new Date(current),
        activities: acts,
        key: `${dateKey}-${generateRandomCode()}`,
      });

      current.setDate(current.getDate() + 1);
    }

    return allDays;
  }, [activities, dateRange]);

  const handleActionsOnActivityClick = (
    action,
    module = 'global',
    vo = 'global',
  ) => {
    dispatch(
      renderDialog({
        id: 'WORKFLOW_FORM',
        props: {
          action,
          voId: vo,
          moduleCode: module,
        },
      }),
    );
  };

  const handleActivitySelect = useCallback(
    (activityCode: string) => {
      setSelectedActivity(activityCode);

      const activity = activities.find((a) => a.code === activityCode);
      if (!activity) return;

      setIsActivityCompleted(activity.meeting.is_completed);

      if (activity.calendar_item || revyreConfig?.length) {
        setCalendarItem(activity.calendar_item);
        dispatch(
          getCalendarDetails({
            code: activity?.calendar_item?.code || activity?.code,
            type: revyreConfig?.length ? 'revyre' : 'legacy',
          }),
        );
      } else {
        setCalendarItem(undefined);
        dispatch(getLeadData({ code: activity.code }));
      }
    },
    [activities, dispatch, revyreConfig?.length],
  );

  useEffect(() => {
    if (!groupedActivities.length) return;

    const todayActivity = getTodayActivity(groupedActivities);
    let activityToSelect;

    if (todayActivity && todayActivity !== 'todayAvailable') {
      activityToSelect = todayActivity;
    } else {
      const firstWithActivities = groupedActivities.find(
        (g) => g.activities.length > 0,
      );
      activityToSelect = firstWithActivities?.activities?.[0];
    }

    if (activityToSelect && activityToSelect.code !== selectedActivity) {
      handleActivitySelect(activityToSelect.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedActivities]);

  const handleDateRangeChange = (dates: [Date, Date]) => {
    setDateRange(dates);
  };

  return (
    <Widget
      title="Calendar"
      actions={
        revyreConfig.length === 0 && (
          <Button
            type="outlined"
            onClick={() => handleActionsOnActivityClick('create_task')}
          >
            {locale(Keys.CREATE_ACTIVITY)}
          </Button>
        )
      }
      content={
        <div className={styles.activityCalendarCard}>
          <div className={styles.activityCalendarCard__header}>
            <RangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              format="dd/MM/yyyy"
              placeholderText={{
                start: 'Select start date',
                end: 'Select end date',
              }}
            />

            <Select
              options={activityTypeOptions}
              value={activityTypeOptions[0].code}
              onChange={(selected) => {
                const selectedCode = selected?.[0]?.value || '';
                setTaskCode(selectedCode);
              }}
              placeholder="All Activities"
              classNames={classnames(
                styles.activityCalendarCard__header__select,
              )}
            />
          </div>
          <div className={styles.activityCalendarCard__content}>
            <Card classNames={styles.activityCalendarCard__content}>
              <div className={styles.activityCalendarCard__content__leftPanel}>
                {groupedActivities.length === 0 ? (
                  <div className={styles.activityCalendarCard__content__empty}>
                    <Text>No activities found</Text>
                  </div>
                ) : (
                  groupedActivities.map((group) => (
                    <div key={group.key}>
                      <div
                        key={format(group.date, 'yyyy-MM-dd')}
                        className={
                          styles.activityCalendarCard__content__dateGroup
                        }
                      >
                        <Text>{format(group.date, 'EEE, MMM dd, yyyy')}</Text>
                      </div>
                      <div
                        className={
                          styles.activityCalendarCard__content__noMeetings
                        }
                      >
                        {group.activities.length === 0 ? (
                          <Text type="label">No Meetings</Text>
                        ) : (
                          <>
                            {group.activities.map((activity) => (
                              <ActivityListItem
                                key={activity.code}
                                activity={activity}
                                isSelected={selectedActivity === activity.code}
                                onClick={() =>
                                  handleActivitySelect(activity.code)
                                }
                              />
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className={styles.activityCalendarCard__content__rightPanel}>
                {revyreConfig?.length ? (
                  <RevyreActivityDetails entityId={selectedActivity} />
                ) : (
                  <ActivityDetails
                    onAction={handleActionsOnActivityClick}
                    calendarItem={calendarItem}
                    isActivityCompleted={isActivityCompleted}
                  />
                )}
              </div>
            </Card>
          </div>
        </div>
      }
    />
  );
}

export default ActivityCalendar;
